import { useEffect, type ComponentType, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui'
import { cn } from '@/utils'
import { documentTitle } from './pageTitle'
import { StickyPageHeader } from './StickyPageHeader'

/**
 * The one page title band (SA-1125, spec
 * docs/superpowers/specs/2026-10-11-sa1125-page-header-design.md): the page's
 * only h1, at one size and one position, always a child of PageFrame.
 * pageHeader.guard.test.ts fails a page that writes its own h1 or width.
 */

/** The page title's recipe (Dan's ruling, 2026-10-10). The task page's exempt h1 reads it too. */
export const PAGE_TITLE_CLASS = 'text-xl font-semibold tracking-tight text-foreground'
/** An object's title inside a shell (a rotation, a study, a contact): an h2 under the shell's h1. */
export const DETAIL_TITLE_CLASS = 'text-lg font-semibold tracking-tight text-foreground'

export interface PageHeaderProps {
  /** The page's one h1 and its browser-tab title. A string, so the two never drift. */
  title: string
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  /** A string is a neutral Badge after the h1 (the nav tag); a node renders as given (a status chip). */
  tag?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  /** "← <label>", a muted link above the title (Dan's ruling, 2026-10-11). */
  back?: { to: string; label: string }
  /** Pin through StickyPageHeader, whose data-slot the register measures. */
  sticky?: boolean
  /** With sticky: StickyPageHeader's horizontal bleed; the header re-adds the padding. */
  bleedX?: boolean
  /** Under the title row, inside the band: a toolbar, a tab strip. */
  children?: ReactNode
}

export function PageHeader({
  title, icon: Icon, tag, subtitle, actions, back, sticky = false, bleedX = false, children,
}: PageHeaderProps) {
  useEffect(() => {
    const before = document.title
    document.title = documentTitle(title)
    return () => { document.title = before }
  }, [title])

  const header = (
    <header data-slot="page-header" className={cn('border-b border-border pb-4', sticky && bleedX && 'px-4 lg:px-6')}>
      {back && (
        <Link to={back.to} className="text-xs text-muted-foreground hover:underline print:hidden">
          ← {back.label}
        </Link>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-2.5">
          {Icon && <Icon aria-hidden="true" className="mt-1 size-5 shrink-0 text-muted-foreground" />}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className={PAGE_TITLE_CLASS}>{title}</h1>
              {tag ? (typeof tag === 'string' ? <Badge variant="neutral">{tag}</Badge> : tag) : null}
            </div>
            {subtitle && <div className="text-sm text-muted-foreground">{subtitle}</div>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-4 space-y-4">{children}</div>}
    </header>
  )
  return sticky ? <StickyPageHeader bleedX={bleedX}>{header}</StickyPageHeader> : header
}
